import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Package, MapPin, Clock, Check, Pencil, Truck, Shirt, X, AlertCircle, QrCode } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TSHIRT_SIZES = ['S', 'M', 'L', 'XL', 'XXL'];

export default function WearerDashboard() {
  const { user } = useAuth();
  const [myBid, setMyBid] = useState(null);
  const [acceptances, setAcceptances] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fulfillment form state
  const [showFulfillment, setShowFulfillment] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [inBengaluru, setInBengaluru] = useState(false);
  const [shipping, setShipping] = useState({ name: '', phone: '', address: '', pincode: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [bidRes, accRes] = await Promise.all([
        axios.get(`${API}/wearer-bids/mine`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/my/acceptances`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setMyBid(bidRes.data);
      setAcceptances(accRes.data || []);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!selectedSize) errors.size = 'Select a T-shirt size';
    if (inBengaluru) {
      if (!shipping.name.trim() || shipping.name.trim().length < 2) errors.name = 'Full name required';
      if (!/^[6-9]\d{9}$/.test(shipping.phone.replace(/\s/g, ''))) errors.phone = 'Valid 10-digit mobile required';
      if (!shipping.address.trim() || shipping.address.trim().length < 10) errors.address = 'Full address required (min 10 chars)';
      if (!/^560\d{3}$/.test(shipping.pincode.trim())) errors.pincode = 'Valid Bangalore pincode (560xxx)';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCompleteFulfillment = async (acceptanceId) => {
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const payload = {
        tshirt_size: selectedSize,
        in_bengaluru: inBengaluru,
      };
      if (inBengaluru) {
        payload.shipping_name = shipping.name;
        payload.shipping_phone = shipping.phone;
        payload.shipping_address = shipping.address;
        payload.shipping_city = 'Bengaluru';
        payload.shipping_pincode = shipping.pincode;
      }
      await axios.post(`${API}/acceptances/${acceptanceId}/fulfillment`, payload, { withCredentials: true });
      setShowFulfillment(null);
      setSelectedSize('');
      setInBengaluru(false);
      setShipping({ name: '', phone: '', address: '', pincode: '' });
      setFieldErrors({});
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = "w-full bg-white border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] rounded-xl p-3 font-medium text-sm focus:outline-none focus:-translate-y-[1px] focus:shadow-[4px_4px_0px_0px_#121212] transition-all";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F6F1E8' }}>
        <div className="animate-spin w-8 h-8 border-4 border-[#121212] border-t-transparent rounded-full" />
      </div>
    );
  }

  const isPending = user?.approval_status === 'pending';
  const isRejected = user?.approval_status === 'rejected';

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F6F1E8' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="wearer-dashboard-title">
            My T-Shirt
          </h1>
          <p className="text-sm font-medium mt-1" style={{ color: '#666' }}>
            Hey {user?.name?.split(' ')[0] || 'there'}! {isPending ? 'Your application is being reviewed.' : 'Manage your ask and see who picked you.'}
          </p>
        </div>

        {/* Under Review Banner */}
        {isPending && (
          <div className="bg-[#E8C86A]/30 border-[3px] border-[#E8C86A] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-6 mb-8 text-center animate-fade-in-up" data-testid="pending-review-banner">
            <Clock className="w-10 h-10 mx-auto mb-3" strokeWidth={2} style={{ color: '#E8C86A' }} />
            <div className="font-black uppercase text-lg mb-2">Under Review</div>
            <p className="text-sm font-medium mb-1" style={{ color: '#555' }}>
              Your YC acceptance screenshot is being reviewed by our team.
            </p>
            <p className="text-sm font-medium mb-4" style={{ color: '#888' }}>
              You'll be able to place asks and participate in the market once your account is approved. This usually takes less than 24 hours.
            </p>
            <div className="bg-[#121212] rounded-xl p-5 text-center">
              <p className="text-base font-bold mb-2" style={{ color: '#F6F1E8' }}>
                Want us to accept you on priority? 🚀
              </p>
              <p className="text-sm font-medium mb-3" style={{ color: '#aaa' }}>
                Tweet at{' '}
                <a href="https://twitter.com/TanmaiVully" target="_blank" rel="noopener noreferrer" className="font-bold hover:underline" style={{ color: '#FF6B6B' }}>@TanmaiVully</a>{' '}
                &{' '}
                <a href="https://twitter.com/prabhav_guddati" target="_blank" rel="noopener noreferrer" className="font-bold hover:underline" style={{ color: '#FF6B6B' }}>@prabhav_guddati</a>
                {' '} with your thoughts on this and tag your favourite startups too. We'll get back to you with some cool stickers along the way 🙌.
              </p>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent('Hey @ycombinator, @TanmaiVully & @prabhav_guddati made https://www.surfacearea.shop/ to turned everyone at YC Startup School India into a human billboard!')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#E8C86A] text-[#121212] font-black uppercase text-sm border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#F6F1E8] hover:-translate-y-[1px] hover:shadow-[4px_4px_0px_0px_#F6F1E8] active:translate-y-[2px] active:shadow-none transition-all rounded-xl px-10 py-2.5"
                data-testid="tweet-to-expedite-btn"
              >
                𝕏
              </a>
            </div>
          </div>
        )}

        {/* Rejected Banner */}
        {isRejected && (
          <div className="bg-[#FF6B6B]/15 border-[3px] border-[#FF6B6B] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-6 mb-8 text-center animate-fade-in-up" data-testid="rejected-banner">
            <AlertCircle className="w-10 h-10 mx-auto mb-3" strokeWidth={2} style={{ color: '#FF6B6B' }} />
            <div className="font-black uppercase text-lg mb-2" style={{ color: '#FF6B6B' }}>Not Approved</div>
            <p className="text-sm font-medium mb-4" style={{ color: '#555' }}>
              Your YC screenshot couldn't be verified. Please send us your LinkedIn, Twitter/X handle, and a proper screenshot of your YC acceptance so we can verify you.
            </p>
            <div
              className="inline-flex items-center gap-2 bg-[#FF6B6B] text-[#121212] font-black uppercase text-sm border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] rounded-xl px-5 py-2.5"
              data-testid="rejected-contact-support-btn"
            >
              Email us at support@surfacearea.shop
            </div>
          </div>
        )}

        {/* Active Bid */}
        <div className="mb-8">
          <h2 className="text-lg font-black uppercase tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Your Ask
          </h2>
          {myBid && myBid.status !== 'withdrawn' ? (
            <div className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-5" data-testid="my-bid-section">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono text-3xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${myBid.asking_price}</div>
                  <div className="text-sm font-medium mt-1" style={{ color: '#666' }}>per T-Shirt</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`border-[2px] border-[#121212] shadow-[2px_2px_0px_0px_#121212] text-xs font-black px-3 py-1 rounded-full uppercase ${myBid.status === 'accepted' ? 'bg-[#6EE7B7]' : 'bg-[#E8C86A]'
                    }`}>
                    {myBid.status}
                  </span>
                  {myBid.status === 'active' && !isPending && !isRejected && (
                    <Link
                      to="/"
                      className="bg-[#F6F1E8] border-[2px] border-[#121212] rounded-lg px-3 py-1.5 shadow-[2px_2px_0px_0px_#121212] text-xs font-black uppercase flex items-center gap-1"
                      data-testid="edit-bid-link"
                    >
                      <Pencil className="w-3 h-3" strokeWidth={3} /> Edit
                    </Link>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-2xl">
              <Package className="w-10 h-10 mx-auto mb-3" strokeWidth={1.5} style={{ color: '#aaa' }} />
              <p className="text-base font-black uppercase mb-2">No active ask</p>
              {!isPending && !isRejected && (
                <Link
                  to="/"
                  className="bg-[#FF6B6B] text-[#121212] font-black uppercase text-sm border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] rounded-xl px-5 py-2 inline-block"
                >
                  Place an Ask
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Acceptances */}
        <h2 className="text-lg font-black uppercase tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Check className="w-5 h-5 inline mr-2 -mt-0.5" strokeWidth={2.5} /> Accepted By
        </h2>

        {acceptances.length === 0 ? (
          <div className="text-center py-12 bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-2xl">
            <Clock className="w-10 h-10 mx-auto mb-3" strokeWidth={1.5} style={{ color: '#aaa' }} />
            <p className="text-base font-black uppercase mb-1">Waiting for a startup</p>
            <p className="text-sm font-medium" style={{ color: '#888' }}>Your ask is live. Startups will pick you when they're ready.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {acceptances.map(a => (
              <div key={a.acceptance_id} className="bg-[#EDE3C7] border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-5" data-testid={`acceptance-${a.acceptance_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl border-[2px] border-[#121212] flex items-center justify-center font-black text-sm" style={{ backgroundColor: '#E8C86A' }}>
                      {(a.startup_name || 'S')[0]}
                    </div>
                    <div>
                      <div className="font-bold text-base">{a.startup_name}</div>
                      <div className="text-xs font-medium" style={{ color: '#888' }}>
                        Accepted {getTimeAgo(a.created_at)} · {a.garment_type}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>${a.asking_price}</div>
                    {a.tshirt_size && (
                      <span className="text-xs font-black uppercase text-[#16a34a]">Size {a.tshirt_size}</span>
                    )}
                  </div>
                </div>

                {/* ═══ Fulfillment Form ═══ */}
                {!a.qr_token && showFulfillment !== a.acceptance_id && (
                  <div className="mt-3 pt-3 border-t-[2px] border-[#121212]/10">
                    <button
                      onClick={() => setShowFulfillment(a.acceptance_id)}
                      className="w-full bg-[#FF6B6B] text-[#121212] font-black uppercase text-sm border-[3px] border-[#121212] shadow-[3px_3px_0px_0px_#121212] hover:-translate-y-[1px] transition-all rounded-xl px-4 py-3 flex items-center justify-center gap-2"
                      data-testid={`start-fulfillment-${a.acceptance_id}`}
                    >
                      <Shirt className="w-4 h-4" strokeWidth={2.5} /> Complete Details & Get QR Code
                    </button>
                  </div>
                )}

                {showFulfillment === a.acceptance_id && (
                  <div className="mt-3 pt-3 border-t-[2px] border-[#121212]/10 animate-fade-in-up space-y-4">
                    {/* T-shirt Size */}
                    <div>
                      <div className="text-xs font-black uppercase tracking-wider mb-2" style={{ color: '#888' }}>
                        <Shirt className="w-3.5 h-3.5 inline mr-1 -mt-0.5" strokeWidth={2.5} /> T-Shirt Size *
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {TSHIRT_SIZES.map(size => (
                          <button
                            key={size}
                            onClick={() => { setSelectedSize(size); setFieldErrors(e => ({ ...e, size: undefined })); }}
                            className={`font-black uppercase text-sm border-[3px] border-[#121212] rounded-xl px-5 py-2 transition-all ${
                              selectedSize === size
                                ? 'bg-[#121212] text-[#F6F1E8] shadow-none translate-y-[2px]'
                                : 'bg-[#F6F1E8] shadow-[3px_3px_0px_0px_#121212] hover:-translate-y-[1px]'
                            }`}
                            data-testid={`size-${size}`}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                      {fieldErrors.size && <p className="text-xs font-bold mt-1" style={{ color: '#FF6B6B' }}>{fieldErrors.size}</p>}
                    </div>

                    {/* Bengaluru Toggle */}
                    <div>
                      <div className="text-xs font-black uppercase tracking-wider mb-2" style={{ color: '#888' }}>
                        <MapPin className="w-3.5 h-3.5 inline mr-1 -mt-0.5" strokeWidth={2.5} /> Are you in Bengaluru?
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => setInBengaluru(true)}
                          className={`flex-1 font-black uppercase text-sm border-[3px] border-[#121212] rounded-xl px-4 py-3 transition-all flex items-center justify-center gap-2 ${
                            inBengaluru
                              ? 'bg-[#6EE7B7] shadow-none translate-y-[2px]'
                              : 'bg-[#F6F1E8] shadow-[3px_3px_0px_0px_#121212] hover:-translate-y-[1px]'
                          }`}
                          data-testid="in-bengaluru-yes"
                        >
                          <Check className="w-4 h-4" strokeWidth={3} /> Yes, I'm in Bengaluru
                        </button>
                        <button
                          onClick={() => setInBengaluru(false)}
                          className={`flex-1 font-black uppercase text-sm border-[3px] border-[#121212] rounded-xl px-4 py-3 transition-all flex items-center justify-center gap-2 ${
                            !inBengaluru
                              ? 'bg-[#E8C86A] shadow-none translate-y-[2px]'
                              : 'bg-[#F6F1E8] shadow-[3px_3px_0px_0px_#121212] hover:-translate-y-[1px]'
                          }`}
                          data-testid="in-bengaluru-no"
                        >
                          <X className="w-4 h-4" strokeWidth={3} /> No
                        </button>
                      </div>
                    </div>

                    {/* Address Fields (Bengaluru only) */}
                    {inBengaluru && (
                      <div className="space-y-3 animate-fade-in-up">
                        <div className="bg-[#6EE7B7]/20 border-[2px] border-[#6EE7B7] rounded-lg px-3 py-2 text-xs font-bold">
                          <Truck className="w-3.5 h-3.5 inline mr-1 -mt-0.5" strokeWidth={2.5} />
                          We'll ship the T-shirt to your Bengaluru address
                        </div>
                        <div>
                          <input data-testid="shipping-name" className={`${inputClass} ${fieldErrors.name ? 'border-[#FF6B6B]' : ''}`} placeholder="Full Name *" value={shipping.name} onChange={e => setShipping(p => ({ ...p, name: e.target.value }))} />
                          {fieldErrors.name && <p className="text-xs font-bold mt-1" style={{ color: '#FF6B6B' }}>{fieldErrors.name}</p>}
                        </div>
                        <div>
                          <input data-testid="shipping-phone" className={`${inputClass} ${fieldErrors.phone ? 'border-[#FF6B6B]' : ''}`} placeholder="Phone (10 digits) *" value={shipping.phone} onChange={e => setShipping(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} />
                          {fieldErrors.phone && <p className="text-xs font-bold mt-1" style={{ color: '#FF6B6B' }}>{fieldErrors.phone}</p>}
                        </div>
                        <div>
                          <input data-testid="shipping-address" className={`${inputClass} ${fieldErrors.address ? 'border-[#FF6B6B]' : ''}`} placeholder="Full Address *" value={shipping.address} onChange={e => setShipping(p => ({ ...p, address: e.target.value }))} />
                          {fieldErrors.address && <p className="text-xs font-bold mt-1" style={{ color: '#FF6B6B' }}>{fieldErrors.address}</p>}
                        </div>
                        <div>
                          <input data-testid="shipping-pincode" className={`${inputClass} ${fieldErrors.pincode ? 'border-[#FF6B6B]' : ''}`} placeholder="Pincode (560xxx) *" value={shipping.pincode} onChange={e => setShipping(p => ({ ...p, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))} />
                          {fieldErrors.pincode && <p className="text-xs font-bold mt-1" style={{ color: '#FF6B6B' }}>{fieldErrors.pincode}</p>}
                        </div>
                      </div>
                    )}

                    {/* Venue Pickup Info */}
                    {!inBengaluru && (
                      <div className="bg-[#E8C86A]/30 border-[2px] border-[#E8C86A] rounded-lg px-3 py-2 text-xs font-bold">
                        📍 You'll pick up your T-shirt at the venue. Show your QR code at check-in.
                      </div>
                    )}

                    {/* Submit */}
                    <button
                      onClick={() => handleCompleteFulfillment(a.acceptance_id)}
                      disabled={submitting}
                      className="w-full bg-[#FF6B6B] text-[#121212] font-black uppercase text-sm border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] hover:-translate-y-[2px] hover:shadow-[6px_6px_0px_0px_#121212] active:translate-y-[4px] active:shadow-none transition-all rounded-xl px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid={`submit-fulfillment-${a.acceptance_id}`}
                    >
                      <QrCode className="w-4 h-4" strokeWidth={3} />
                      {submitting ? 'Generating...' : 'Submit & Get QR Code'}
                    </button>
                    <button onClick={() => { setShowFulfillment(null); setFieldErrors({}); }} className="w-full text-sm font-bold text-center hover:text-[#FF6B6B]">Cancel</button>
                  </div>
                )}

                {/* ═══ QR Code Display ═══ */}
                {a.qr_url && (
                  <div className="mt-4 pt-4 border-t-[2px] border-[#121212]/10">
                    <div className="bg-white border-[3px] border-[#121212] shadow-[4px_4px_0px_0px_#121212] rounded-2xl p-6 text-center">
                      <div className="text-xs font-black uppercase tracking-wider mb-3" style={{ color: '#888' }}>
                        <QrCode className="w-4 h-4 inline mr-1 -mt-0.5" strokeWidth={2.5} /> Your Venue Pass
                      </div>
                      <div className="inline-block p-3 bg-white rounded-xl border-[2px] border-[#121212]/10 mb-3">
                        <QRCodeSVG
                          value={a.qr_url}
                          size={180}
                          level="H"
                          bgColor="#FFFFFF"
                          fgColor="#121212"
                          data-testid={`qr-code-${a.acceptance_id}`}
                        />
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-bold">Show this QR at the venue</p>
                        <p className="text-xs font-medium" style={{ color: '#888' }}>
                          Size {a.tshirt_size} · {a.in_bengaluru ? 'Shipping to Bengaluru' : 'Venue Pickup'} · ${a.asking_price}
                        </p>
                        {a.checked_in && (
                          <div className="mt-2 inline-flex items-center gap-1 bg-[#6EE7B7] border-[2px] border-[#121212] rounded-full px-3 py-1 text-xs font-black uppercase">
                            <Check className="w-3 h-3" strokeWidth={3} /> Checked In
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Completed fulfillment info (no QR yet - legacy) */}
                {a.fulfillment_type && !a.qr_url && (
                  <div className="mt-3 pt-3 border-t-[2px] border-[#121212]/10 text-xs font-mono" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#888' }}>
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" strokeWidth={2.5} /> {a.fulfillment_type === 'ship_to_me' ? `Shipping to ${a.shipping_city}` : 'Venue pickup'}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function getTimeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
